import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UserPlus, Pencil, Trash2, ShieldCheck, X, Loader2, AlertTriangle, RefreshCcw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Client-side "Users" screen — only visible to client_user + client_role='super_admin'.
 * Scoped strictly to the caller's own organisation (backend enforces this too).
 */
export default function ClientTeamPage() {
  const { user } = useAuth();
  const allowed = user?.role === "client_user" && user?.client_role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | user object
  const [toDelete, setToDelete] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/users");
      setItems(data || []);
    } catch (err) { setError(apiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter((u) => u.is_active).length;
    const superAdmins = items.filter((u) => u.client_role === "super_admin").length;
    return { total, active, superAdmins };
  }, [items]);

  if (!allowed) {
    return (
      <div className="mx-auto max-w-md rounded-xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="team-access-restricted">
        <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
        <h2 className="mt-3 text-lg font-semibold text-amber-900">Access Restricted</h2>
        <p className="mt-2 text-sm text-amber-800">
          Only Super Admins can manage users in your organisation. Ask your Super Admin to promote you if you need access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Users</h2>
          <p className="text-muted-foreground">Manage colleagues from <span className="font-medium text-slate-900">{user?.client_name}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="team-refresh">
            <RefreshCcw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" onClick={() => setEditing("new")} data-testid="team-add-btn">
            <UserPlus className="mr-1 h-4 w-4" /> Add User
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Total Users" value={stats.total} />
        <StatCard label="Active" value={stats.active} tone="green" />
        <StatCard label="Super Admins" value={stats.superAdmins} tone={stats.superAdmins > 0 ? "indigo" : undefined} />
      </div>

      {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-10 text-center text-muted-foreground">Loading users…</p>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">
              No colleagues yet. Click <span className="font-semibold">Add User</span> to invite one — they'll receive a welcome email with a temporary password.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50/50 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((u) => {
                    const isMe = u.id === user.id;
                    return (
                      <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`team-row-${u.email}`}>
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {u.full_name || <span className="text-slate-400">—</span>}
                          {isMe && <span className="ml-1 text-xs text-slate-400">(you)</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{u.email}</td>
                        <td className="px-4 py-3">
                          {u.client_role === "super_admin"
                            ? <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100"><ShieldCheck className="mr-1 h-3 w-3" /> Super Admin</Badge>
                            : <Badge variant="outline" className="border-slate-300 text-slate-600">User</Badge>}
                        </td>
                        <td className="px-4 py-3">
                          {u.is_active
                            ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>
                            : <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Inactive</Badge>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => setEditing(u)} data-testid={`team-edit-${u.email}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => setToDelete(u)}
                              disabled={isMe}
                              title={isMe ? "You can't remove yourself" : "Remove user"}
                              data-testid={`team-delete-${u.email}`}
                            >
                              <Trash2 className={`h-3.5 w-3.5 ${isMe ? "opacity-30" : "text-rose-600"}`} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <TeamUserForm
          userItem={editing === "new" ? null : editing}
          selfId={user.id}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent data-testid="team-delete-dialog">
          <DialogHeader>
            <DialogTitle>Remove {toDelete?.full_name || toDelete?.email}?</DialogTitle>
            <DialogDescription>
              This user will lose portal access immediately. Their notes and attachments on tickets remain unchanged. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try { await api.delete(`/users/${toDelete.id}`); setToDelete(null); load(); }
                catch (err) { setError(apiError(err)); setToDelete(null); }
              }}
              data-testid="team-delete-confirm"
            >
              Remove User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------------------------------------------------------------------------

function StatCard({ label, value, tone }) {
  const toneClass = tone === "green" ? "text-emerald-600" : tone === "indigo" ? "text-indigo-600" : "text-slate-900";
  return (
    <Card className="border-slate-200">
      <CardContent className="p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className={`mt-2 text-3xl font-bold tracking-tight ${toneClass}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function TeamUserForm({ userItem, selfId, onClose, onSaved }) {
  const isEdit = !!userItem;
  const [email, setEmail] = useState(userItem?.email || "");
  const [fullName, setFullName] = useState(userItem?.full_name || "");
  const [isSuperAdmin, setIsSuperAdmin] = useState(userItem?.client_role === "super_admin");
  const [isActive, setIsActive] = useState(userItem?.is_active ?? true);
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isSelf = isEdit && userItem?.id === selfId;

  async function submit() {
    if (!email.trim()) { setError("Email is required."); return; }
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        const payload = {
          full_name: fullName.trim(),
          role: "client_user",
          client_id: userItem.client_id,
          is_active: isActive,
          client_role: isSuperAdmin ? "super_admin" : "user",
        };
        if (newPassword.trim()) payload.new_password = newPassword.trim();
        await api.put(`/users/${userItem.id}`, payload);
      } else {
        await api.post("/users", {
          email: email.trim().toLowerCase(),
          full_name: fullName.trim(),
          role: "client_user",
          is_active: isActive,
          client_role: isSuperAdmin ? "super_admin" : "user",
        });
      }
      onSaved();
    } catch (err) { setError(apiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="team-form-sheet">
        <SheetHeader>
          <SheetTitle>{isEdit ? `Edit ${userItem.full_name || userItem.email}` : "Add User"}</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          <div>
            <label className="text-sm font-medium">Email *</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEdit}
              className="mt-1"
              placeholder="colleague@your-org.com"
              data-testid="team-form-email"
            />
            {!isEdit && <p className="mt-1 text-xs text-slate-500">A welcome email with a temporary password will be sent to this address.</p>}
          </div>
          <div>
            <label className="text-sm font-medium">Full Name</label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-1" placeholder="Jane Doe" data-testid="team-form-name" />
          </div>
          <div className="flex items-start justify-between rounded-lg border border-slate-200 bg-white p-3">
            <div>
              <p className="text-sm font-medium">Super Admin</p>
              <p className="mt-0.5 text-xs text-slate-500">
                Super Admins can add, edit and remove users in your organisation.
              </p>
            </div>
            <Switch
              checked={isSuperAdmin}
              onCheckedChange={setIsSuperAdmin}
              disabled={isSelf}
              data-testid="team-form-super-admin"
            />
          </div>
          <div className="flex items-start justify-between rounded-lg border border-slate-200 bg-white p-3">
            <div>
              <p className="text-sm font-medium">Active</p>
              <p className="mt-0.5 text-xs text-slate-500">Inactive users can't sign in.</p>
            </div>
            <Switch
              checked={isActive}
              onCheckedChange={setIsActive}
              disabled={isSelf}
              data-testid="team-form-active"
            />
          </div>
          {isEdit && (
            <div>
              <label className="text-sm font-medium">Reset Password (optional)</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1"
                placeholder="Leave blank to keep current password"
                data-testid="team-form-new-password"
              />
            </div>
          )}
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}><X className="mr-1 h-4 w-4" /> Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="team-form-submit">
              {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {isEdit ? "Save Changes" : "Send Invite"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
